import { buildImageSelection } from './buildImageSelection'
import {
  detectReferenceFileType,
  EnabledReferenceFileTypes,
  ReferenceFileType,
} from './detectReferenceFileType'
import { extractEmlContent } from './extractEmlContent'
import { extractPdfText } from './extractPdfText'
import {
  EmlSelection,
  ImageSelection,
  PdfSelection,
} from './referenceSelectionTypes'
import { renderPdfThumbnail } from './renderPdfThumbnail'

const MAX_PDF_SIZE_BYTES = 20971520 // 20MB
// Kept under content-api's 9MB JSON body limit: base64 inflates raw bytes by
// ~4/3, so 6MB of source image data becomes ~8MB of base64 text.
const MAX_IMAGE_SIZE_BYTES = 6291456 // 6MB
const MAX_EML_SIZE_BYTES = 15728640 // 15MB — .eml text can be bulky with inline base64 images
const MAX_HTML_FILE_SIZE_BYTES = 1048576 // 1MB
// Keep in sync with ImportContentModal's paste textarea maxLength.
const MAX_HTML_CONTENT_LENGTH = 100000

const extractTextSafely = async (file: File) => {
  try {
    const buffer = await file.arrayBuffer()
    return { success: true as const, text: await extractPdfText(buffer) }
  } catch {
    return { success: false as const, text: null }
  }
}

const renderThumbnailSafely = async (file: File) => {
  try {
    const buffer = await file.arrayBuffer()
    return await renderPdfThumbnail(buffer)
  } catch {
    // Thumbnail is best-effort — a render failure shouldn't block import as
    // long as text extraction succeeded.
    return null
  }
}

const readFileAsText = async (file: File): Promise<string> => {
  if (typeof file.text === 'function') {
    return file.text()
  }
  const buffer = await file.arrayBuffer()
  return new TextDecoder('utf-8').decode(buffer)
}

const maxSizeForType = (type: ReferenceFileType): number => {
  if (type === 'pdf') {
    return MAX_PDF_SIZE_BYTES
  }
  if (type === 'image') {
    return MAX_IMAGE_SIZE_BYTES
  }
  if (type === 'html') {
    return MAX_HTML_FILE_SIZE_BYTES
  }
  return MAX_EML_SIZE_BYTES
}

const disabledTypeMessage = (type: ReferenceFileType): string => {
  if (type === 'pdf') {
    return 'PDF import is not enabled.'
  }
  if (type === 'image') {
    return 'Image import is not enabled.'
  }
  if (type === 'html') {
    return 'HTML import is not enabled.'
  }
  return 'Email import is not enabled.'
}

export type ProcessReferenceFileSuccess =
  | { kind: 'pdf'; selection: PdfSelection }
  | { kind: 'image'; selection: ImageSelection }
  | { kind: 'eml'; selection: EmlSelection }
  // HTML files are not kept as a file selection — their contents are dumped
  // into the paste textarea so the user can review/edit before adding.
  | { kind: 'html'; content: string }

export type ProcessReferenceFileResult =
  | { ok: true; value: ProcessReferenceFileSuccess }
  | { ok: false; error: string }

export const processReferenceFile = async (
  file: File,
  enabledFileTypes: EnabledReferenceFileTypes,
  onProcessing: (kind: Exclude<ReferenceFileType, 'html'>, file: File) => void
): Promise<ProcessReferenceFileResult> => {
  const detected = detectReferenceFileType(file)

  if (!detected) {
    return {
      ok: false,
      error:
        'This file type is not supported. Use PDF, EML, JPG, PNG, or HTML.',
    }
  }

  if (!enabledFileTypes[detected]) {
    return { ok: false, error: disabledTypeMessage(detected) }
  }

  if (file.size > maxSizeForType(detected)) {
    return {
      ok: false,
      error: 'This file is too large. Please try a smaller file.',
    }
  }

  // HTML uploads skip the processing/selection UI — they dump into paste.
  if (detected === 'html') {
    try {
      const raw = await readFileAsText(file)
      const content = raw.slice(0, MAX_HTML_CONTENT_LENGTH)
      if (!content.trim()) {
        return {
          ok: false,
          error:
            'No content was found in this HTML file. Try a different file.',
        }
      }
      return { ok: true, value: { kind: 'html', content } }
    } catch {
      return {
        ok: false,
        error: 'Could not read this HTML file. Please try a different file.',
      }
    }
  }

  onProcessing(detected, file)

  if (detected === 'pdf') {
    const [textOutcome, thumbnailUrl] = await Promise.all([
      extractTextSafely(file),
      renderThumbnailSafely(file),
    ])

    if (!textOutcome.success || !textOutcome.text?.trim()) {
      return {
        ok: false,
        error: textOutcome.success
          ? 'No readable text was found in this PDF. Try a different file.'
          : 'Could not read this PDF. Please try a different file.',
      }
    }

    return {
      ok: true,
      value: {
        kind: 'pdf',
        selection: {
          file,
          thumbnailUrl,
          extractedText: textOutcome.text,
          isProcessing: false,
          error: null,
        },
      },
    }
  }

  if (detected === 'image') {
    const selection = await buildImageSelection(file)
    if (!selection.dataUri) {
      if (selection.previewUrl) {
        URL.revokeObjectURL(selection.previewUrl)
      }
      return {
        ok: false,
        error:
          selection.error ??
          'Could not read this image. Please try a different file.',
      }
    }
    return { ok: true, value: { kind: 'image', selection } }
  }

  try {
    const buffer = await file.arrayBuffer()
    const { subject, html: emlHtml } = await extractEmlContent(buffer)

    if (!emlHtml) {
      return {
        ok: false,
        error: 'No HTML content was found in this email. Try a different file.',
      }
    }

    return {
      ok: true,
      value: {
        kind: 'eml',
        selection: {
          file,
          subject,
          html: emlHtml,
          isProcessing: false,
          error: null,
        },
      },
    }
  } catch {
    return {
      ok: false,
      error: 'Could not read this email. Please try a different file.',
    }
  }
}
